import { LTTB } from 'downsample';
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { getDICOMDate } from '~/domain/dicom/utils';
import { CurveData } from '~/domain/patient/dvh';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';
import useDose from '~/hooks/patient/dose/useDose';
import useDvh from '~/hooks/patient/dose/useDvh';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { optimizationObjectivesState } from '~/state/atoms/dose/optimizationObjectivesAtom';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { relativeDosePercent } from '~/utils/dose/doseDisplayValidation';
import { buildDvhPoints } from '~/utils/dvhCurve';
import {
    createCSVContent,
    createJSONContent,
    csvEscapeCell,
    downloadCreatedFile,
    exportFileStamp,
} from '~/utils/io/files/handleFileExport';
import {
    buildOptimizationObjectivesCsv,
    buildOptimizationObjectivesJson,
} from '~/utils/io/files/optimizationObjectives';

export interface DoseStatisticsExport {
    /**
     * Downloads every structure's DVH curve as CSV under the dose's
     * metadata. The relative dose cell of each row is empty when the
     * normalization dose is not a finite dose above 0 Gy. Returns false,
     * and downloads nothing, when no structure has a curve.
     */
    readonly exportDvhCsv: () => Promise<boolean>;
    /** Downloads the min, max and mean dose per structure as CSV. */
    readonly exportStatisticsCsv: () => void;
    /** Downloads the min, max and mean dose per structure as JSON. */
    readonly exportStatisticsJson: () => void;
    /**
     * Downloads the optimization objectives as CSV, one row per constraint,
     * under the rows that name the dose.
     */
    readonly exportObjectivesCsv: () => void;
    /** Downloads the optimization objectives as JSON, one entry per constraint. */
    readonly exportObjectivesJson: () => void;
}

/** The identifiers an exported statistics file names its dose by. */
interface ExportedDoseIdentity {
    readonly patientId: string;
    readonly doseUid: string;
    readonly structureSetLabel: string;
    readonly imageSeriesDescription: string;
}

/** The structures' statistics, sorted by name, each dose to three decimals. */
function statisticsRows(curves: ReadonlyArray<CurveData>) {
    return [...curves]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((curve) => ({
            structure: curve.name,
            doseMinGy: curve.doseMinGy.toFixed(3),
            doseMaxGy: curve.doseMaxGy.toFixed(3),
            doseMeanGy: curve.doseMeanGy.toFixed(3),
        }));
}

/**
 * The downloads the dose statistics offer for the selected dose: the
 * statistics of the given curves as CSV or JSON, every structure's DVH
 * curve as CSV, and the optimization objectives as CSV or JSON. Each file
 * is named with the date and time of the export.
 */
export function useDoseStatisticsExport(
    curves: ReadonlyArray<CurveData>
): DoseStatisticsExport {
    const { dose } = useDose();
    const currentDose = useCurrentDose();
    const { getAllDvhs } = useDvh();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const objectives = useRecoilValue(optimizationObjectivesState);
    const selectedPatient = useSelectedPatient();

    const identity = useCallback((): ExportedDoseIdentity => {
        if (
            !selectedPatient?.dataSet ||
            !currentDose ||
            !currentDose.structureSet
        ) {
            return {
                patientId: '',
                doseUid: '',
                structureSetLabel: '',
                imageSeriesDescription: '',
            };
        }
        return {
            patientId: selectedPatient.dataSet.PatientID,
            doseUid: currentDose.dicomUid,
            structureSetLabel: currentDose.structureSet.structureSetLabel,
            imageSeriesDescription:
                currentDose.structureSet.image?.seriesMetadata
                    .seriesDescription ?? '',
        };
    }, [selectedPatient, currentDose]);

    // The CSV rows that name the dose an exported table belongs to: a
    // header row and a value row.
    const identityCsvSection = useCallback((): string => {
        const {
            patientId,
            doseUid,
            structureSetLabel,
            imageSeriesDescription,
        } = identity();
        const summaryHeaderRow = [
            'PatientID',
            'StructureSetLabel',
            'DoseUID',
            'ImageSeriesDescription',
        ];
        const summaryDataRows = [
            [patientId, structureSetLabel, doseUid, imageSeriesDescription].map(
                csvEscapeCell
            ),
        ];
        return [summaryHeaderRow, ...summaryDataRows]
            .map((row) => row.join(','))
            .join('\n');
    }, [identity]);

    const exportStatisticsCsv = useCallback(() => {
        const summarySection = identityCsvSection();
        const doseHeaderRow = [
            'Structure',
            'Min Dose (Gy)',
            'Max Dose (Gy)',
            'Mean Dose (Gy)',
        ];
        const doseDataRows = statisticsRows(curves).map((row) => [
            csvEscapeCell(row.structure),
            row.doseMinGy,
            row.doseMaxGy,
            row.doseMeanGy,
        ]);
        const doseSection = [doseHeaderRow, ...doseDataRows]
            .map((row) => row.join(','))
            .join('\n');
        const csvUri = createCSVContent('', [
            `${summarySection}\n\n${doseSection}`,
        ]);
        downloadCreatedFile(
            csvUri,
            `dose_statistics_${exportFileStamp(new Date())}.csv`
        );
    }, [curves, identityCsvSection]);

    const exportStatisticsJson = useCallback(() => {
        const jsonUri = createJSONContent(statisticsRows(curves));
        downloadCreatedFile(
            jsonUri,
            `dose_statistics_${exportFileStamp(new Date())}.json`
        );
    }, [curves]);

    const exportObjectivesCsv = useCallback(() => {
        const csvUri = createCSVContent('', [
            `${identityCsvSection()}\n\n${buildOptimizationObjectivesCsv(
                objectives
            )}`,
        ]);
        downloadCreatedFile(
            csvUri,
            `optimization_objectives_${exportFileStamp(new Date())}.csv`
        );
    }, [objectives, identityCsvSection]);

    const exportObjectivesJson = useCallback(() => {
        downloadCreatedFile(
            createJSONContent(buildOptimizationObjectivesJson(objectives)),
            `optimization_objectives_${exportFileStamp(new Date())}.json`
        );
    }, [objectives]);

    const exportDvhCsv = useCallback(async (): Promise<boolean> => {
        const roiDvhs = await getAllDvhs();
        if (!roiDvhs || roiDvhs.length === 0) {
            console.warn('No DVH data available to export.');
            return false;
        }
        const now = new Date();
        const currentDate = getDICOMDate(now);
        const globalMetadata = [
            `Patient Name: ${
                selectedPatient?.dataSet.PatientName || 'Unknown'
            }`,
            `Patient ID: ${selectedPatient?.dataSet.PatientID || 'Unknown'}`,
            `Plan: ${dose?.planLabel || 'Unknown'}`,
            `Comment: ${dose?.doseComment || 'Unknown'}`,
            `Export Date: ${currentDate}`,
            ``,
        ].join('\n');
        const doseHeader =
            'Dose [Gy], Relative Dose [%], Ratio of Total Structure Volume [%]';
        const normalizationGy = dose?.normalizationValueGy;
        const relativeDoseCell = (doseGy: number): string => {
            const percent =
                normalizationGy === undefined
                    ? undefined
                    : relativeDosePercent(doseGy, normalizationGy);
            return percent === undefined ? '' : percent.toFixed(3);
        };
        const doseDataRows = roiDvhs.map((roiDvh) => {
            const structure = currentStructureSet?.roiArray.find(
                (roi) => roi.roiNumber === roiDvh.roiNumber
            );
            const roiVolume = roiDvh.dvh.structureVolumeCm3 || 0;
            const roiMetadata = [
                `Structure: ${roiDvh.name}`,
                `Approval Status: ${structure?.approvalStatus || 'Unknown'}`,
                `Volume [cm³]: ${roiVolume.toFixed(3)}`,
                `Min Dose [Gy]: ${roiDvh.dvh.doseMinGy.toFixed(3)}`,
                `Max Dose [Gy]: ${roiDvh.dvh.doseMaxGy.toFixed(3)}`,
                `Mean Dose [Gy]: ${roiDvh.dvh.doseMeanGy.toFixed(3)}`,
                `Equiv. Sphere Diam. [cm]: ${(
                    2 * Math.cbrt((3 * roiVolume) / (4 * Math.PI))
                ).toFixed(3)}`,
                ``,
            ].join('\n');
            // Trimmed and clamped exactly as the on-screen curve is, so the
            // exported record and the display cannot disagree about how far
            // the curve extends — the comparison SRV-WOR-H-10-2-1 prescribes.
            //
            // Scale 1 is deliberate, not an oversight: the CSV carries the
            // dose as measured, never the display rescaling, so an exported
            // record means the same thing whatever the viewer happened to be
            // rescaled to. The min/max/mean header rows and the relative-dose
            // column are unscaled for the same reason. A rescaled dose will
            // therefore read higher on screen than in its export — expected;
            // do not "fix" it by threading doseScale through here.
            const originalData = buildDvhPoints(
                roiDvh.dvh,
                dose?.maxDoseGy ?? roiDvh.dvh.doseMaxGy,
                1
            );
            const downsampledData = LTTB(originalData, 1000) as {
                x: number;
                y: number;
            }[];
            const doseRows: string[] = [];
            downsampledData.forEach((point) => {
                if (point.x >= 0) {
                    doseRows.push(
                        `${point.x.toFixed(3)}, ${relativeDoseCell(
                            point.x
                        )}, ${point.y.toFixed(3)}`
                    );
                }
            });
            return `${roiMetadata}\n${doseHeader}\n${doseRows.join('\n')}`;
        });
        const csvContent = `${globalMetadata}\n${doseDataRows.join('\n\n')}`;
        const csvUri = createCSVContent('', [csvContent]);
        downloadCreatedFile(csvUri, `dvh_export_${exportFileStamp(now)}.csv`);
        return true;
    }, [getAllDvhs, selectedPatient, dose, currentStructureSet]);

    return {
        exportDvhCsv,
        exportStatisticsCsv,
        exportStatisticsJson,
        exportObjectivesCsv,
        exportObjectivesJson,
    };
}
